import { reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import type { User, LoginRequest, RegisterRequest } from '@/api/types'
import { apiClient, ApiClientError } from '@/api'
import { createLogger } from '@/utils/logger'
import * as Sentry from '@sentry/vue'
import { ROLES } from '@/constants/roles'

const logger = createLogger('useAuthState')

// TypeScript types for auth-expired event
interface AuthExpiredEventDetail {
  message: string
}

// LocalStorage keys
const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_INFO: 'user_info',
  EMPLOYEE_INFO: 'employee_info',
  DEV_USER: 'dev-user',
} as const

// Readiness flag (declared before initializeUserFromStorage uses it)
let isAuthStateReady = false

// Initialize from localStorage on module load (singleton pattern)
const initializeUserFromStorage = (): User | null => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (!token) {
      logger.log('No auth token found, skipping user initialization')
      isAuthStateReady = true
      return null
    }

    const customerData = localStorage.getItem(STORAGE_KEYS.USER_INFO)
    const employeeData = localStorage.getItem(STORAGE_KEYS.EMPLOYEE_INFO)

    if (customerData) {
      const user = JSON.parse(customerData)
      // Defensive: Ensure user_roles is always an array
      if (!Array.isArray(user.user_roles)) {
        logger.warn('user_roles is not an array, fixing to empty array', {
          current_type: typeof user.user_roles,
          current_value: user.user_roles,
        })
        user.user_roles = []
      }
      logger.log('Initialized user from localStorage (customer)', {
        email: user.email,
        user_type: user.user_type,
        user_roles: JSON.stringify(user.user_roles || []),
        has_roles: Array.isArray(user.user_roles),
        roles_length: user.user_roles?.length || 0,
      })
      isAuthStateReady = true
      return user
    }
    if (employeeData) {
      const user = JSON.parse(employeeData)
      // Defensive: Ensure user_roles is always an array
      if (!Array.isArray(user.user_roles)) {
        logger.warn('user_roles is not an array, fixing to empty array', {
          current_type: typeof user.user_roles,
          current_value: user.user_roles,
        })
        user.user_roles = []
      }
      logger.log('Initialized user from localStorage (employee)', {
        email: user.email,
        user_type: user.user_type,
        user_roles: JSON.stringify(user.user_roles || []),
        has_roles: Array.isArray(user.user_roles),
        roles_length: user.user_roles?.length || 0,
      })
      isAuthStateReady = true
      return user
    }
    logger.log('No user data found in localStorage despite having token')
    isAuthStateReady = true
    return null
  } catch (error) {
    logger.warn('Failed to initialize user from storage:', error)
    isAuthStateReady = true
    return null
  }
}

// Singleton reactive state - Hydrate from localStorage on initialization
const initialUser = initializeUserFromStorage()
const authState = reactive<{
  user: User | null
  isLoading: boolean
  error: string | null
  lastFetch: number | null
  sessionExpiredMessage: string | null
}>({
  user: initialUser, // Fix: Hydrate from storage
  isLoading: false,
  error: null,
  lastFetch: initialUser ? Date.now() : null, // Mark as fetched if user exists
  sessionExpiredMessage: null,
})

/**
 * Set Sentry user context for error tracking
 * Attaches user ID and tags to all error events
 * Email is included but will be scrubbed by beforeSend hook
 *
 * @param user - User object or null to clear context
 */
const setSentryUserContext = (user: User | null) => {
  if (user) {
    // Set user context (email will be scrubbed by beforeSend)
    Sentry.setUser({
      id: user.user_id,
      email: user.email, // Will be scrubbed by beforeSend hook
    })

    // Set user_type tag
    Sentry.setTag('user_type', user.user_type)

    // Set user_role tag for employees with roles
    if (user.user_type === 'employee' && user.user_roles?.length > 0) {
      // Use the first role as the primary role tag
      Sentry.setTag('user_role', user.user_roles[0])
    }

    logger.log('Sentry user context set', {
      user_id: user.user_id,
      user_type: user.user_type,
      user_roles: user.user_roles,
    })
  } else {
    // Clear user context
    Sentry.setUser(null)
    logger.log('Sentry user context cleared')
  }
}

// Set initial Sentry context if user was loaded from storage
if (initialUser) {
  setSentryUserContext(initialUser)
}

// Computed properties - User Type (order matters for dependency resolution)
const isAuthenticated = computed(() => authState.user !== null)
const isCustomer = computed(() => authState.user?.user_type === 'customer')
const isEmployee = computed(() => authState.user?.user_type === 'employee')

// CRITICAL: userRoles MUST be defined BEFORE role check computeds that use it
const userRoles = computed(() => authState.user?.user_roles || [])

// Computed properties - Role Checks (depend on userRoles above)
/**
 * Returns true if user has ROLES.ADMIN_SUPER or ROLES.ADMIN_MANAGER role
 */
const isAdmin = computed(() => {
  const roles = userRoles.value
  return (
    roles.includes(ROLES.ADMIN_SUPER) || roles.includes(ROLES.ADMIN_MANAGER)
  )
})

/**
 * Returns true if user has ROLES.SERVICE_PROVIDER role
 */
const isServiceProvider = computed(() =>
  userRoles.value.includes(ROLES.SERVICE_PROVIDER)
)

/**
 * Returns true if user has ROLES.ADMIN_SUPER role
 */
const isSuperAdmin = computed(() => userRoles.value.includes(ROLES.ADMIN_SUPER))

// Utility functions for role checking
const hasRole = (role: string): boolean => userRoles.value.includes(role)

const hasAnyRole = (...roles: string[]): boolean =>
  roles.some(role => userRoles.value.includes(role))

// Module-level event listener setup for auth-expired event (singleton pattern)
// This runs once when the module is first imported
const handleAuthExpiredEvent = (event: Event) => {
  const customEvent = event as CustomEvent<AuthExpiredEventDetail>
  logger.warn('Auth expired event received:', customEvent.detail.message)

  // Store the message so components can display it
  authState.sessionExpiredMessage = customEvent.detail.message

  // Clear user state
  authState.user = null
  authState.lastFetch = null
  authState.error = null

  // Clear Sentry user context
  setSentryUserContext(null)

  // Clear localStorage
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_INFO)
    localStorage.removeItem(STORAGE_KEYS.EMPLOYEE_INFO)
    logger.log('Cleared user from localStorage due to auth expiration')
  } catch (_error) {
    logger.warn('Failed to clear user from localStorage:', _error)
  }
}

window.addEventListener('auth-expired', handleAuthExpiredEvent)

export function useAuthState() {
  // Lazy router access to avoid injection errors during router initialization
  // Router is only needed for logout navigation, so we defer access until then
  let router: ReturnType<typeof useRouter> | null = null

  const getRouter = () => {
    if (!router) {
      try {
        router = useRouter()
      } catch {
        // Router not available yet (during initialization)
        logger.warn('Router not available yet in useAuthState')
      }
    }
    return router
  }

  // State mutations
  const setUser = (user: User | null) => {
    authState.user = user
    authState.lastFetch = user ? Date.now() : null
    authState.error = null

    if (user) {
      logger.log('User state updated', {
        email: user.email,
        user_type: user.user_type,
        user_roles: JSON.stringify(user.user_roles || []),
        has_roles: Array.isArray(user.user_roles),
        roles_length: user.user_roles?.length || 0,
        roles_are_defined: user.user_roles !== undefined,
        roles_are_null: user.user_roles === null,
      })
    } else {
      logger.log('User state cleared (set to null)')
    }
  }

  const setLoading = (loading: boolean) => {
    authState.isLoading = loading
  }

  const setError = (error: string | null) => {
    authState.error = error
  }

  const clearUser = () => {
    authState.user = null
    authState.lastFetch = null
    authState.error = null
    logger.log('User state cleared')
  }

  // LocalStorage management - centralized in one place
  const saveUserToStorage = (user: User, userType: 'customer' | 'employee') => {
    try {
      const storageKey =
        userType === 'customer'
          ? STORAGE_KEYS.USER_INFO
          : STORAGE_KEYS.EMPLOYEE_INFO

      logger.log('Saving user to localStorage', {
        storage_key: storageKey,
        user_type: userType,
        email: user.email,
        user_roles: JSON.stringify(user.user_roles || []),
        has_roles: Array.isArray(user.user_roles),
        roles_length: user.user_roles?.length || 0,
        roles_are_defined: user.user_roles !== undefined,
      })

      const userJson = JSON.stringify(user)
      localStorage.setItem(storageKey, userJson)

      // Verify what was saved
      const savedData = localStorage.getItem(storageKey)
      if (savedData) {
        const parsed = JSON.parse(savedData)
        logger.log('Verified saved data in localStorage', {
          storage_key: storageKey,
          saved_roles: JSON.stringify(parsed.user_roles || []),
          matches_input:
            JSON.stringify(parsed.user_roles) ===
            JSON.stringify(user.user_roles),
        })
      }
    } catch (error) {
      logger.warn('Failed to save user to localStorage:', error)
    }
  }

  const loadUserFromStorage = (
    userType: 'customer' | 'employee'
  ): User | null => {
    try {
      const storageKey =
        userType === 'customer'
          ? STORAGE_KEYS.USER_INFO
          : STORAGE_KEYS.EMPLOYEE_INFO
      const storedUser = localStorage.getItem(storageKey)
      if (storedUser) {
        const user = JSON.parse(storedUser)
        // Defensive: Ensure user_roles is always an array
        if (!Array.isArray(user.user_roles)) {
          logger.warn(
            'user_roles is not an array in loadUserFromStorage, fixing to empty array',
            {
              current_type: typeof user.user_roles,
              current_value: user.user_roles,
            }
          )
          user.user_roles = []
        }
        logger.log('Loaded user from localStorage', {
          storage_key: storageKey,
          user_type: userType,
          email: user.email,
          user_roles: JSON.stringify(user.user_roles || []),
          has_roles: Array.isArray(user.user_roles),
          roles_length: user.user_roles?.length || 0,
          roles_are_defined: user.user_roles !== undefined,
        })
        return user
      } else {
        logger.log(`No user data found in localStorage for key: ${storageKey}`)
      }
    } catch (error) {
      logger.warn(`Failed to load user from localStorage:`, error)
    }
    return null
  }

  const clearUserFromStorage = () => {
    try {
      // Clear both customer and employee storage keys
      localStorage.removeItem(STORAGE_KEYS.USER_INFO)
      localStorage.removeItem(STORAGE_KEYS.EMPLOYEE_INFO)
      // Note: We don't clear auth_token here as ApiClient manages it
      logger.log('Cleared user from localStorage')
    } catch (error) {
      logger.warn('Failed to clear user from localStorage:', error)
    }
  }

  // Check if we need to refetch user (cache invalidation)
  const shouldRefetchUser = (maxAgeMs: number = 60000): boolean => {
    if (!authState.lastFetch) return true
    return Date.now() - authState.lastFetch > maxAgeMs
  }

  // Auth operations - Merged from useAuth
  const login = async (
    credentials: LoginRequest,
    userType: 'customer' | 'employee' = 'customer'
  ): Promise<
    | boolean
    | {
        requirePasswordChange?: boolean
        requiresVerification?: boolean
        canResend?: boolean
        retryAfter?: number
      }
  > => {
    setLoading(true)
    setError(null)

    try {
      logger.log(`🔑 Calling ${userType} login API...`)
      const response =
        userType === 'customer'
          ? await apiClient.customerLogin(credentials)
          : await apiClient.employeeLogin(credentials)

      // Log the complete API response
      logger.log('📡 Raw API response:', JSON.stringify(response, null, 2))

      // Log the user object from the response
      logger.log(
        '👤 User from API response:',
        JSON.stringify(response.user, null, 2)
      )

      // Validate user_roles in the API response
      if (!response.user.user_roles) {
        logger.error('❌ CRITICAL: API response missing user_roles field!')
      } else if (!Array.isArray(response.user.user_roles)) {
        logger.error(
          '❌ CRITICAL: API response user_roles is not an array!',
          typeof response.user.user_roles
        )
      } else if (response.user.user_roles.length === 0) {
        logger.warn('⚠️ WARNING: API response user_roles array is empty')
      } else {
        logger.log(
          `✅ API response user_roles: [${response.user.user_roles.join(', ')}]`
        )
      }

      // Set user in state FIRST (synchronous)
      logger.log('📝 Setting user in authState...')
      setUser(response.user)
      logger.log('✅ User set in authState:', response.user.email)

      // Verify user_roles after setUser
      if (authState.user && authState.user.user_roles) {
        logger.log(
          `✅ user_roles in authState after setUser: [${authState.user.user_roles.join(', ')}]`
        )
      } else {
        logger.error('❌ CRITICAL: user_roles lost after setUser!')
      }

      // THEN save to localStorage SECOND (always save to match token persistence)
      // This fixes iOS logout issues where token persists but user info doesn't
      logger.log('💾 Saving user to localStorage...')
      saveUserToStorage(response.user, userType)
      logger.log('✅ User saved to localStorage')

      // Set Sentry user context for error tracking
      setSentryUserContext(response.user)

      // Check if password change is required
      if (response.require_password_change) {
        logger.log('🔑 Password change required for user')
        return { requirePasswordChange: true }
      }

      // Check if email verification is required
      if (response.requires_verification) {
        logger.log('📧 Email verification required for user')
        return {
          requiresVerification: true,
          canResend: response.can_resend,
          retryAfter: response.retry_after,
        }
      }

      logger.log('✅ Login successful - returning true')
      return true
    } catch (err) {
      let errorMessage = 'Login failed. Please try again.'

      if (err instanceof ApiClientError) {
        if (err.statusCode === 401) {
          errorMessage = 'Invalid email or password. Please try again.'
        } else if (err.isNetworkError) {
          errorMessage =
            'Network error. Please check your connection and try again.'
        } else {
          errorMessage = err.message
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      logger.error('❌ Login failed with error:', errorMessage)
      setError(errorMessage)
      return false
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData: RegisterRequest): Promise<boolean> => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.customerRegister(userData)

      logger.log('Registration response received:', response)

      // Handle email verification flow - registration returns message without token
      // User must verify their email before they can login
      if (response.requires_verification) {
        logger.log('Email verification required, registration successful')
        return true
      }

      // Validate response structure (legacy flow with token)
      if (!response || !response.token || !response.user) {
        throw new Error('Invalid response from registration')
      }

      // Validate user data structure
      if (
        !response.user.user_id ||
        !response.user.email ||
        !response.user.user_type
      ) {
        logger.error('Invalid user data structure:', response.user)
        throw new Error('Invalid user data received from server')
      }

      logger.log('Response validation passed, setting user state')

      // Set user state (registration now returns auth data with token)
      setUser(response.user)

      // Store user info with error handling
      saveUserToStorage(response.user, 'customer')
      logger.log('User data stored in localStorage')

      // Set Sentry user context for error tracking
      setSentryUserContext(response.user)

      logger.log('Registration successful, user set:', authState.user)
      return true
    } catch (err) {
      logger.error('Registration error:', err)

      // Clear any partial state
      clearUser()

      let errorMessage = 'Registration failed. Please try again.'

      if (err instanceof ApiClientError) {
        if (err.statusCode === 409) {
          errorMessage = 'An account with this email already exists.'
        } else if (err.isNetworkError) {
          errorMessage =
            'Network error. Please check your connection and try again.'
        } else {
          errorMessage = err.message
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      setError(errorMessage)
      return false
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    // Clear state FIRST before any async operations
    clearUser()
    clearUserFromStorage()

    // Clear Sentry user context
    setSentryUserContext(null)

    try {
      await apiClient.logout()
    } catch (err) {
      // Even if logout fails, state is already cleared
      logger.warn('Logout request failed:', err)
    }

    // Navigate to login after logout
    const currentRouter = getRouter()
    if (currentRouter) {
      currentRouter.replace('/login')
    }
  }

  const getCurrentUser = async (): Promise<User | null> => {
    try {
      // Check if we have a fresh cached user
      if (authState.user && !shouldRefetchUser()) {
        logger.log('Returning cached user')
        return authState.user
      }

      logger.log('Fetching current user from API')
      const user = await apiClient.getCurrentUser()
      setUser(user)

      // Set Sentry user context for error tracking
      setSentryUserContext(user)

      return user
    } catch (err) {
      // Log the error but don't set error state since this is often called silently
      if (err instanceof ApiClientError) {
        logger.error(
          `Failed to get current user: ${err.message} (status: ${err.statusCode})`
        )
      } else {
        logger.error('Failed to get current user:', err)
      }
      clearUser()

      // Clear Sentry context on failure
      setSentryUserContext(null)

      return null
    }
  }

  const requestPasswordReset = async (_email: string): Promise<boolean> => {
    setLoading(true)
    setError(null)

    try {
      // For now, simulate the request since we don't have this endpoint yet
      await new Promise(resolve => setTimeout(resolve, 1500))
      return true
    } catch (_err) {
      setError('Failed to reset password. Please try again.')
      return false
    } finally {
      setLoading(false)
    }
  }

  const verifyEmail = async (token: string): Promise<boolean> => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.verifyEmail(token)
      setUser(response.user)
      saveUserToStorage(response.user, 'customer')

      // Set Sentry user context for error tracking
      setSentryUserContext(response.user)

      logger.log('Email verification successful')
      return true
    } catch (err) {
      let errorMessage = 'Email verification failed. Please try again.'

      if (err instanceof ApiClientError) {
        errorMessage = err.message
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      setError(errorMessage)
      return false
    } finally {
      setLoading(false)
    }
  }

  const resendVerification = async (
    email: string
  ): Promise<{ success: boolean; message: string }> => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.resendVerification(email)
      logger.log('Verification email resent successfully')
      return response
    } catch (err) {
      let errorMessage =
        'Failed to resend verification email. Please try again.'

      if (err instanceof ApiClientError) {
        errorMessage = err.message
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Session expired message management
  const getSessionExpiredMessage = (): string | null => {
    return authState.sessionExpiredMessage
  }

  const clearSessionExpiredMessage = () => {
    authState.sessionExpiredMessage = null
  }

  return {
    // State (readonly via computed)
    user: computed(() => authState.user),
    isLoading: computed(() => authState.isLoading),
    error: computed(() => authState.error),
    lastFetch: computed(() => authState.lastFetch),
    isAuthStateReady: computed(() => isAuthStateReady),

    // Computed flags - User Type
    isAuthenticated,
    isCustomer,
    isEmployee,
    userRoles,

    // Computed flags - Role Checks
    isAdmin,
    isServiceProvider,
    isSuperAdmin,

    // Utility functions - Role Checking
    hasRole,
    hasAnyRole,

    // Mutations
    setUser,
    setLoading,
    setError,
    clearUser,

    // LocalStorage management
    saveUserToStorage,
    loadUserFromStorage,
    clearUserFromStorage,

    // Cache management
    shouldRefetchUser,

    // Auth operations (merged from useAuth)
    login,
    register,
    logout,
    getCurrentUser,
    requestPasswordReset,
    verifyEmail,
    resendVerification,

    // Session expired message management
    getSessionExpiredMessage,
    clearSessionExpiredMessage,

    // Storage keys (for internal use)
    STORAGE_KEYS,
  }
}
